import {
  Box,
  Button,
  Paper,
  Stack,
  TextField,
  Typography,
  Alert,
} from "@mui/material";
import React, { useState } from "react";
import { watermarkPdfBuffer } from "../../utility/watermark-pdf";

const ADMIN_PASSWORD = "iv-watermark-2026";
const SESSION_KEY = "iv-admin-watermark-unlocked";

function PasswordGate({ onUnlock }: { onUnlock: () => void }) {
  const [pw, setPw] = useState("");
  const [error, setError] = useState(false);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (pw === ADMIN_PASSWORD) {
      sessionStorage.setItem(SESSION_KEY, "1");
      onUnlock();
    } else {
      setError(true);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "#f5f5f5",
      }}
    >
      <Paper sx={{ p: 4, width: 360 }} elevation={2}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Watermark-Tool
        </Typography>
        <form onSubmit={submit}>
          <Stack spacing={2}>
            <TextField
              type="password"
              label="Passwort"
              value={pw}
              onChange={(e) => {
                setPw(e.target.value);
                setError(false);
              }}
              error={error}
              helperText={error ? "Falsches Passwort" : undefined}
              autoFocus
              fullWidth
            />
            <Button type="submit" variant="contained" fullWidth>
              Entsperren
            </Button>
          </Stack>
        </form>
      </Paper>
    </Box>
  );
}

function WatermarkForm() {
  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canSubmit =
    file != null && name.trim().length > 0 && email.trim().length > 0 && !busy;

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] ?? null;
    setFile(f);
    setError(null);
  };

  const submit = async () => {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      const buffer = await file.arrayBuffer();
      const watermarked = await watermarkPdfBuffer(buffer, {
        name: name.trim(),
        email: email.trim(),
      });

      const blob = new Blob([watermarked], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const baseName = file.name.replace(/\.pdf$/i, "");
      const safeName = name.trim().replace(/[^\p{L}\p{N}\s\-_]/gu, "").replace(/\s+/g, "_");
      const a = document.createElement("a");
      a.href = url;
      a.download = `${baseName}_${safeName || "watermarked"}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(err?.message ?? "Watermarking fehlgeschlagen.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f5f5f5", py: 6 }}>
      <Box sx={{ maxWidth: 560, mx: "auto", px: 2 }}>
        <Typography variant="h5" sx={{ mb: 1 }}>
          Reporting watermarken
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          PDF hochladen, Empfaenger eintragen, Watermark wird clientseitig
          eingefuegt und das Dokument zum Download angeboten.
        </Typography>

        <Paper sx={{ p: 3 }} elevation={1}>
          <Stack spacing={2.5}>
            <Box>
              <Button variant="outlined" component="label" fullWidth>
                {file ? file.name : "PDF auswaehlen"}
                <input
                  hidden
                  type="file"
                  accept="application/pdf"
                  onChange={handleFile}
                />
              </Button>
            </Box>

            <TextField
              label="Name des Empfaengers"
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
            />
            <TextField
              label="E-Mail des Empfaengers"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              fullWidth
            />

            {error && <Alert severity="error">{error}</Alert>}

            <Button
              variant="contained"
              size="large"
              disabled={!canSubmit}
              onClick={submit}
            >
              {busy ? "Watermarking..." : "Watermarken & Download"}
            </Button>
          </Stack>
        </Paper>
      </Box>
    </Box>
  );
}

export default function WatermarkTool() {
  const [unlocked, setUnlocked] = useState<boolean>(
    () => sessionStorage.getItem(SESSION_KEY) === "1"
  );

  if (!unlocked) {
    return <PasswordGate onUnlock={() => setUnlocked(true)} />;
  }
  return <WatermarkForm />;
}
